import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../../../context/AuthContext';
import {
  getChecklists, createChecklist, updateChecklist, deleteChecklist,
  getComments, createComment, updateComment, deleteComment, updateCommentFolder,
  getTaskFiles, uploadTaskFiles, createTaskFolder, renameTaskFolder,
  updateTask, deleteTask
} from '../../../api/pmsApi';
// pmsApi에 deleteTaskFolder API가 없는 경우 직접 fetch로 대체
import ChecklistTab from './TaskDrawer/ChecklistTab';
import CommentsTab from './TaskDrawer/CommentsTab';
import FilesTab from './TaskDrawer/FilesTab';
import SettingsTab from './TaskDrawer/SettingsTab';

function TaskDrawerPanel({ task, project, onClose, onTaskUpdate, onTaskDelete, defaultTab = 'checklist', onTaskDataChanged, categoryName, focusId }) {
  const { token, user } = useContext(AuthContext);



  // 상태 관리
  const [activeTab, setActiveTab] = useState(defaultTab); // checklist, comments, files, settings

  useEffect(() => {
    if (defaultTab) setActiveTab(defaultTab);
  }, [defaultTab]);

  // [권한 제어] 순수 참조자 여부 판별
  const isProjectMaster = (() => {
    if (user?.role === 'admin') return true;
    return String(project?.author_id) === String(user?.id) ||
      String(project?.author_id) === String(user?.userid) ||
      String(project?.leader_id) === String(user?.id) ||
      String(project?.leader_id) === String(user?.userid) ||
      String(project?.reviewer_id) === String(user?.id) ||
      String(project?.reviewer_id) === String(user?.userid);
  })();

  const isProjectMember = project?.members?.some(m =>
    String(m.user_id) === String(user?.id) ||
    String(m.user_id) === String(user?.userid) ||
    (m.user && String(m.user.id) === String(user?.id)) ||
    (m.user && String(m.user.userid) === String(user?.userid))
  );

  const allMembers = task?.members || [];
  const assignees = allMembers.filter(m => m.is_manager !== false);
  const isAssignee = assignees.some(a => String(a.user_id) === String(user?.id) || String(a.user_id) === String(user?.userid));
  const isPureWatcher = !isProjectMaster && !isProjectMember && !isAssignee;

  const [checklists, setChecklists] = useState([]);
  const [newChecklist, setNewChecklist] = useState('');

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');

  // 파일 첨부 관련 (코멘트 작성용)
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);

  // 진행률 (설정 탭 용)
  const [progressRate, setProgressRate] = useState(task?.progress_rate || 0);

  // 상세 설명 수정용 상태
  const [descContent, setDescContent] = useState(task?.description || '');
  const [isDescExpanded, setIsDescExpanded] = useState(false); // 기본으로 접혀있게 설정
  const [isFullScreenMode, setIsFullScreenMode] = useState(false); // 리스트/코멘트 집중(전체보기) 모드

  // 폴더 관련 (파일 탭)
  const [folders, setFolders] = useState([]); // 서버에서 로드된 폴더 리스트
  const [newFolderName, setNewFolderName] = useState('');
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [targetFileId, setTargetFileId] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState('');

  // 폴더명 변경 모달 상태
  const [editFolderModalVisible, setEditFolderModalVisible] = useState(false);
  const [selectedEditFolder, setSelectedEditFolder] = useState('');
  const [newEditFolderName, setNewEditFolderName] = useState('');

  const openEditFolderModal = (folderName) => {
    setSelectedEditFolder(folderName);
    setNewEditFolderName(folderName);
    setEditFolderModalVisible(true);
  };

  const handleEditFolderSubmit = async () => {
    if (!newEditFolderName.trim()) {
      alert('변경할 폴더명을 입력하세요.');
      return;
    }

    try {
      const res = await renameTaskFolder(task.id, selectedEditFolder, newEditFolderName.trim(), token);
      if (res.ok) {
        // 클라이언트 상태 업데이트 (폴더명 변경)
        setFolders(prev => prev.map(f => f === selectedEditFolder ? newEditFolderName.trim() : f));
        setComments(prev => prev.map(c =>
          c.folder_name === selectedEditFolder ? { ...c, folder_name: newEditFolderName.trim() } : c
        ));

        alert(`[${selectedEditFolder}] 폴더명이 [${newEditFolderName.trim()}]으로 변경되었습니다.`);
        setEditFolderModalVisible(false);
        onTaskDataChanged?.(task.id);
      }
    } catch (e) {
      console.error('Folder Rename Error:', e);
      alert('폴더명 변경 중 오류가 발생했습니다.');
    }
  };

  // 폴더 삭제 모달 상태
  const [deleteFolderModalVisible, setDeleteFolderModalVisible] = useState(false);
  const [selectedDeleteFolder, setSelectedDeleteFolder] = useState('');

  const openDeleteFolderModal = (folderName) => {
    setSelectedDeleteFolder(folderName);
    setDeleteFolderModalVisible(true);
  };

  const handleDeleteFolderSubmit = async (deleteMode) => {
    // deleteMode: 'folder_only' | 'folder_and_files'
    const confirmMessage = deleteMode === 'folder_only'
      ? `[${selectedDeleteFolder}] 폴더를 삭제하시겠습니까?\n(내부 파일들은 보존되어 밖으로 이동됩니다.)`
      : `[${selectedDeleteFolder}] 폴더와 그 안의 모든 파일들을 영구 삭제하시겠습니까?\n(이 작업은 되돌릴 수 없습니다!)`;

    if (!window.confirm(confirmMessage)) {
      return; // 사용자가 취소하면 중단
    }

    try {
      const res = await fetch(`/api/pms/tasks/${task.id}/folders/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ folderName: selectedDeleteFolder, deleteMode })
      });
      const data = await res.json();
      if (data.ok) {
        if (deleteMode === 'folder_only') {
          // 폴더만 삭제: 폴더 목록에서 제거하고 내부 파일들의 폴더명을 지워 밖으로 꺼냄
          setFolders(prev => prev.filter(f => f !== selectedDeleteFolder));
          setComments(prev => prev.map(c =>
            c.folder_name === selectedDeleteFolder ? { ...c, folder_name: null } : c
          ));
        } else {
          // 폴더와 파일 모두 삭제: 폴더 및 내부 파일을 목록에서 완전히 제거
          setFolders(prev => prev.filter(f => f !== selectedDeleteFolder));
          setComments(prev => prev.filter(c => c.folder_name !== selectedDeleteFolder));
        }
        setDeleteFolderModalVisible(false);
        onTaskDataChanged?.(task.id);
        // 서버와 동기화를 위해 재조회 권장 (하지만 위에서 UI 상태 업데이트로 일단 반영됨)
        // loadComments(true); loadFilesAndFolders(true); 
      } else {
        alert('폴더 삭제 실패: ' + (data.message || ''));
      }
    } catch (e) {
      console.error('Folder Delete Error:', e);
      alert('폴더 삭제 중 오류가 발생했습니다.');
    }
  };

  // 답글(대댓글) 
  const [replyTargetId, setReplyTargetId] = useState(null);
  const [replyContent, setReplyContent] = useState('');

  useEffect(() => {
    if (task && token) {
      loadChecklists();
      loadComments();
      loadFilesAndFolders();
      setProgressRate(task.progress_rate || 0);
      setDescContent(task.description || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task, token]);

  useEffect(() => {
    if (activeTab === 'comments' && comments.length === 0) setIsFullScreenMode(false);
    if (activeTab === 'checklist' && checklists.length === 0) setIsFullScreenMode(false);
  }, [comments, checklists, activeTab]);

  const loadChecklists = async () => {
    try {
      const res = await getChecklists(task.id, token);
      if (res.ok) setChecklists(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const loadComments = async (triggerRefresh = false) => {
    try {
      const res = await getComments(task.id, token);
      if (res.ok) {
        setComments(res.data);
        if (triggerRefresh) onTaskDataChanged?.(task.id);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const loadFilesAndFolders = async (triggerRefresh = false) => {
    try {
      const res = await getTaskFiles(task.id, token);
      if (res.ok) {
        const folderNames = res.data.filter(f => f.is_folder).map(f => f.file_name).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
        setFolders(folderNames);
        if (triggerRefresh) onTaskDataChanged?.(task.id);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // 체크리스트 핸들러
  const handleAddChecklist = async (e) => {
    e.preventDefault();
    if (!newChecklist.trim()) return;
    try {
      const res = await createChecklist(task.id, { content: newChecklist }, token);
      if (res.ok) {
        setChecklists([res.data, ...checklists]);
        setNewChecklist('');
      }
    } catch (e) {
      alert('체크리스트 추가 실패');
    }
  };

  const handleToggleChecklist = async (id, currentStatus) => {
    try {
      const res = await updateChecklist(id, { is_completed: !currentStatus }, token);
      if (res.ok) {
        setChecklists(checklists.map(c => c.id === id ? res.data : c));
      }
    } catch (e) {
      alert('상태 변경 실패');
    }
  };

  const handleEditChecklist = async (id, newContent) => {
    try {
      const res = await updateChecklist(id, { content: newContent }, token);
      if (res.ok) {
        setChecklists(checklists.map(c => c.id === id ? res.data : c));
      }
    } catch (e) {
      alert('내용 수정 실패');
    }
  };

  const handleDeleteChecklist = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try {
      const res = await deleteChecklist(id, token);
      if (res.ok) {
        setChecklists(checklists.filter(c => c.id !== id));
      }
    } catch (e) {
      alert('삭제 실패');
    }
  };

  // 무작위 6자리 난수를 포함한 고유 파일명 생성 로직 (B안)
  const generateUniqueFile = (origFile) => {
    if (!origFile) return null;
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');

    const randomDigits = Math.floor(Math.random() * 1000000).toString().padStart(6, '0');
    const uniqueKey = `_${y}${m}${d}F${randomDigits}`;

    const lastDot = origFile.name.lastIndexOf('.');
    let newName;
    if (lastDot !== -1) {
      newName = `${origFile.name.substring(0, lastDot)}${uniqueKey}${origFile.name.substring(lastDot)}`;
    } else {
      newName = `${origFile.name}${uniqueKey}`;
    }

    return new File([origFile], newName, { type: origFile.type });
  };

  // 코멘트/파일 핸들러
  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() && selectedFiles.length === 0) return;

    if (selectedFiles.length > 0) {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        const uniqueFile = generateUniqueFile(file);
        formData.append('files', uniqueFile);
      });
      if (newComment.trim()) {
        formData.append('content', newComment);
      }
      try {
        const res = await uploadTaskFiles(task.id, formData, token);
        if (res.ok) {
          setSelectedFiles([]);
          setNewComment('');
          loadComments(true);
        }
      } catch (e) {
        alert('파일 업로드 실패');
      }
    } else {
      try {
        const res = await createComment(task.id, { content: newComment }, token);
        if (res.ok) {
          setComments([res.data, ...comments]);
          setNewComment('');
          onTaskDataChanged?.(task.id);
        }
      } catch (e) {
        alert('코멘트 추가 실패');
      }
    }
  };

  const handleReplySubmit = async (parentId, content, files = [], targetMention = null) => {
    if (!content.trim() && files.length === 0) return;

    if (files.length > 0) {
      const formData = new FormData();
      files.forEach(file => {
        const uniqueFile = generateUniqueFile(file);
        formData.append('files', uniqueFile);
      });
      formData.append('parent_id', parentId);
      if (content.trim()) formData.append('content', content);
      if (targetMention) formData.append('target_mention', targetMention);
      try {
        const res = await uploadTaskFiles(task.id, formData, token);
        if (res.ok) {
          setReplyTargetId(null);
          setReplyContent('');
          loadComments(true);
        }
      } catch (e) {
        alert('답글 첨부파일 업로드 실패');
      }
    } else {
      try {
        const res = await createComment(task.id, { content, parent_id: parentId, target_mention: targetMention }, token);
        if (res.ok) {
          setReplyTargetId(null);
          setReplyContent('');
          loadComments(true);
        }
      } catch (e) {
        alert('답글 등록 실패');
      }
    }
  };

  const handleEditCommentSubmit = async (commentId, content, files = [], originalCreatedAt) => {
    if (!content.trim() && files.length === 0) return;
    try {
      if (content.trim()) {
        if (commentId) {
          await updateComment(commentId, { content }, token);
        } else {
          await createComment(task.id, { content, created_at: originalCreatedAt }, token);
        }
      }
      if (files.length > 0) {
        const formData = new FormData();
        files.forEach(file => {
          const uniqueFile = generateUniqueFile(file);
          formData.append('files', uniqueFile);
        });
        if (originalCreatedAt) {
          formData.append('created_at', originalCreatedAt);
        }
        await uploadTaskFiles(task.id, formData, token);
      }
      loadComments(true);
      return true;
    } catch (e) {
      console.error(e);
      alert('코멘트 수정 실패');
      return false;
    }
  };

  const handleDeleteComment = async (id) => {
    const hasChildren = comments.some(c => c.parent_id === id);
    const confirmMsg = hasChildren 
      ? '이 항목을 삭제하면 하위에 달린 첨부파일도 함께 영구 삭제됩니다.\n정말 삭제하시겠습니까?' 
      : '삭제하시겠습니까?';

    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await deleteComment(id, token);
      if (res.ok) {
        setComments(comments.filter(c => c.id !== id && c.parent_id !== id));
        onTaskDataChanged?.(task.id);
      }
    } catch (e) {
      alert('삭제 실패');
    }
  };

  const handleCreateFolder = async (folderName) => {
    try {
      const res = await createTaskFolder(task.id, folderName, token);
      if (res.ok) {
        loadFilesAndFolders(true);
      }
    } catch (e) {
      alert('폴더 생성 실패');
    }
  };

  // 폴더 변경 모달 핸들러
  const openFolderModal = (fileId) => {
    setTargetFileId(fileId);
    setSelectedFolder(null); // 매번 모달 열 때 선택 초기화
    setShowFolderModal(true);
  };

  const handleMoveFolder = async () => {
    if (selectedFolder === null || selectedFolder === undefined) {
      alert('이동할 폴더를 선택하세요.');
      return;
    }

    // 현재 폴더와 선택한 폴더가 같은지 체크
    const targetFile = comments.find(c => String(c.id) === String(targetFileId));
    const currentFolder = targetFile ? (targetFile.folder_name || '') : '';

    if (currentFolder === selectedFolder) {
      alert('현재 위치와 동일한 폴더입니다.');
      return;
    }

    try {
      await updateCommentFolder(targetFileId, selectedFolder === '' ? null : selectedFolder, token);

      // 상태 즉시 업데이트 (해당 코멘트의 folder_name 변경)
      setComments(prevComments => prevComments.map(c =>
        String(c.id) === String(targetFileId) ? { ...c, folder_name: selectedFolder === '' ? null : selectedFolder } : c
      ));

      alert(selectedFolder === '' ? '최상위로 이동되었습니다.' : `[${selectedFolder}] 폴더로 이동되었습니다.`);
      setShowFolderModal(false);
      setSelectedFolder(null); // 초기화
      onTaskDataChanged?.(task.id);
    } catch (e) {
      console.error(e);
      alert('폴더 이동에 실패했습니다.');
    }
  };

  // 드래그 앤 드롭 폴더 이동 핸들러 (모달 없이 즉시 이동)
  const handleDragMoveFolder = async (commentId, folderName) => {
    try {
      const targetFolder = folderName === '' ? null : folderName;

      // 이미 해당 폴더에 있으면 무시 (API 호출 방지)
      const targetFile = comments.find(c => String(c.id) === String(commentId));
      if (targetFile && (targetFile.folder_name || null) === targetFolder) return;

      await updateCommentFolder(commentId, targetFolder, token);
      setComments(prevComments => prevComments.map(c =>
        String(c.id) === String(commentId) ? { ...c, folder_name: targetFolder } : c
      ));
      onTaskDataChanged?.(task.id);
    } catch (e) {
      console.error(e);
      alert('폴더 이동에 실패했습니다.');
    }
  };

  // 설정 탭 완료율 저장
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setIsFullScreenMode(false); // 어떤 탭으로 이동하든 무조건 기본 화면으로 리셋
  };

  const handleSaveProgress = async (formData, newProgressRate) => {
    try {
      const formatUpdateDate = (dateVal) => {
        if (!dateVal) return null;
        try {
          // 문자열이면 T 기준 앞부분(YYYY-MM-DD)만 잘라서 반환
          if (typeof dateVal === 'string') return dateVal.split('T')[0];

          // 객체(DatePicker가 준 이상한 객체 포함)라면 강제로 순수 Date로 생성
          const d = new Date(dateVal);
          if (isNaN(d.getTime())) return null;

          // 오프셋 계산 없이 한국/로컬 시간에 맞는 년-월-일 직접 추출
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        } catch (e) {
          return null;
        }
      };

      const updateData = {
        title: formData.title,
        description: formData.description,
        start_date: formatUpdateDate(formData.startDate),
        end_date: formatUpdateDate(formData.endDate),
        status: formData.status,
        progress_rate: newProgressRate !== undefined ? newProgressRate : progressRate,
        assignee_id: formData.assignee_id,
        watchers: formData.watchers,
        sort_order: formData.sortOrder
      };

      // 서버에 저장 후, 서버가 DB에서 꺼내준 최신 데이터를 받아온다
      const resData = await updateTask(task.id, updateData, token);

      alert('업무 정보가 성공적으로 저장되었습니다.');
      const updatedTask = resData?.data || { ...task, ...updateData };
      setProgressRate(updatedTask.progress_rate !== undefined ? updatedTask.progress_rate : updateData.progress_rate);

      // 서버가 반환한 최신 데이터를 부모 컴포넌트에 전달 (리스트 실시간 갱신)
      if (onTaskUpdate) {
        onTaskUpdate(updatedTask);
      }
      return true;
    } catch (e) {
      console.error(e);
      alert('저장 중 오류가 발생했습니다.');
      return false;
    }
  };

  const [deleteTaskModalVisible, setDeleteTaskModalVisible] = useState(false);

  const handleDeleteTask = () => {
    setDeleteTaskModalVisible(true);
  };

  const handleDeleteTaskSubmit = async () => {
    try {
      await deleteTask(task.id, token);
      if (onTaskDelete) onTaskDelete(task.id);
    } catch (e) {
      alert('업무 삭제에 실패했습니다.');
    }
  };

  if (!task) return null;

  return (
    <div className="d-flex flex-column bg-white shadow-lg position-relative" style={{ width: '100%', height: '100vh', borderLeft: '1px solid #e0e0e0' }}>

      {/* 🚀 상단 헤더 영역 (탭에 따라 동적 변경) */}
      <div className="p-3 d-flex justify-content-between align-items-start" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #adb5bd' }}>
        <div className="d-flex w-100 pe-3 align-items-center">

          {activeTab === 'settings' && project ? (
            /* [프로젝트 뷰 헤더] - 세부설정 탭일 때 상위 프로젝트 정보 노출 */
            <>
              <div className="text-primary rounded-3 d-flex flex-column align-items-center justify-content-center me-3 flex-shrink-0"
                style={{ width: '48px', height: '48px' }}>
                <i className="fas fa-project-diagram fs-3"></i>
              </div>
              <div className="flex-grow-1">
                <div className="d-flex align-items-center mb-2">
                  <h4 className="mb-0 fw-bold text-dark lh-sm me-3" style={{ letterSpacing: '-0.5px' }}>{project.title}</h4>
                  <span className={`badge rounded-pill fw-medium ${project.status === 'in_progress' ? 'bg-primary' : (project.status === 'completed' ? 'bg-success' : 'bg-secondary')}`} style={{ fontSize: '0.75rem', padding: '0.35rem 0.6rem' }}>
                    {project.status === 'completed' ? '완료' : (project.status === 'in_progress' ? '진행중' : '시작전')}
                  </span>
                </div>
                <div className="d-flex align-items-center text-secondary" style={{ fontSize: '0.8rem' }}>
                  <div className="d-flex align-items-center me-3 bg-white rounded px-2 py-1 border shadow-sm" style={{ borderColor: '#dee2e6' }}>
                    <i className="far fa-calendar-alt text-muted me-2"></i>
                    <span className="fw-medium text-dark">{project.start_date} ~ {project.end_date}</span>
                  </div>
                  <div className="d-flex align-items-center bg-white rounded px-2 py-1 border shadow-sm" style={{ borderColor: '#dee2e6' }}>
                    <i className="fas fa-chart-line text-muted me-2"></i>
                    <span className="fw-medium text-dark">진척도: {project.progress_rate}%</span>
                    <div className="progress shadow-sm ms-2" style={{ width: '150px', height: '8px', borderRadius: '4px', backgroundColor: '#ced4da' }}>
                      <div className="progress-bar bg-primary" style={{ width: `${project.progress_rate}%`, borderRadius: '4px' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* [기존 세부업무 헤더] - 코멘트, 파일 등 나머지 탭 */
            <>
              <div className="text-white rounded-3 d-flex flex-column align-items-center justify-content-center shadow-sm me-3 flex-shrink-0"
                style={{ width: '48px', height: '48px', background: 'linear-gradient(135deg, #4263eb 0%, #748ffc 100%)' }}>
                <i className="fas fa-layer-group fs-5"></i>
              </div>
              <div className="flex-grow-1">
                <div className="d-flex align-items-center mb-2">
                  <h4 className="mb-0 fw-bold text-dark lh-sm me-3" style={{ letterSpacing: '-0.5px' }}>{task.title}</h4>
                  <span className={`badge rounded-pill fw-medium ${task.status === 'in_progress' ? 'bg-primary' : (task.status === 'completed' ? 'bg-success' : 'bg-secondary')}`}
                    style={{ fontSize: '0.75rem', padding: '0.35rem 0.6rem' }}>
                    {task.status === 'completed' ? '완료' : (task.status === 'in_progress' ? '진행중' : '시작전')}
                  </span>
                </div>
                <div className="d-flex align-items-center text-secondary" style={{ fontSize: '0.8rem' }}>
                  <div className="d-flex align-items-center me-3 bg-white rounded px-2 py-1 border shadow-sm" style={{ borderColor: '#dee2e6' }}>
                    <i className="far fa-calendar-alt text-muted me-2"></i>
                    <span className="fw-medium text-dark">{task.start_date ? task.start_date.split('T')[0] : '-'} ~ {task.end_date ? task.end_date.split('T')[0] : '-'}</span>
                  </div>
                  <div className="d-flex align-items-center bg-white rounded px-2 py-1 border shadow-sm" style={{ borderColor: '#dee2e6' }}>
                    <i className="fas fa-chart-line text-muted me-2"></i>
                    <span className="fw-medium text-dark">진척도: {progressRate}%</span>
                    <div className="progress shadow-sm ms-2" style={{ width: '150px', height: '8px', borderRadius: '4px', backgroundColor: '#ced4da' }}>
                      <div className="progress-bar bg-primary" style={{ width: `${progressRate}%`, borderRadius: '4px' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* 투명하고 심플한 닫기 버튼 */}
        <button className="btn btn-link text-muted p-1 border-0 shadow-none d-flex align-items-center justify-content-center flex-shrink-0 mt-1"
          onClick={onClose} style={{ textDecoration: 'none' }} title="닫기"
          onMouseOver={(e) => e.currentTarget.style.color = '#fa5252'}
          onMouseOut={(e) => e.currentTarget.style.color = '#6c757d'}>
          <i className="fas fa-times fs-5"></i>
        </button>
      </div>

      {/* 🚀 상세 설명 영역 (접기/펼치기 기능 포함) */}
      {!isFullScreenMode && (
        <div className="p-3 border-bottom" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center">
              <i className="fas fa-align-left text-secondary me-2 fs-5"></i>
              <h5 className="fw-bold text-dark mb-0 fs-5">상세 설명</h5>
            </div>
            <button
              className="btn btn-sm bg-white border text-secondary shadow-sm"
              style={{ fontSize: '0.85rem', padding: '0.2rem 0.6rem' }}
              onClick={() => setIsDescExpanded(!isDescExpanded)}
            >
              {isDescExpanded ? (
                <>접기 <i className="fas fa-chevron-up ms-1"></i></>
              ) : (
                <>펼치기 <i className="fas fa-chevron-down ms-1"></i></>
              )}
            </button>
          </div>

          {isDescExpanded && (
            <div className="animation-fade-in mt-2">
              <div className="p-3 bg-white border rounded shadow-sm" style={{ minHeight: '60px', fontSize: '0.875rem', whiteSpace: 'pre-wrap', color: '#343a40' }}>
                {descContent || <span className="text-muted">설명이 없습니다.</span>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 🚀 4대 탭 메뉴 (메인 프로젝트 탭과 동일한 오리지널 스타일) */}
      <div className="pt-2 px-3 border-bottom-0 d-flex justify-content-between align-items-end" style={{ backgroundColor: '#f8f9fa' }}>
        <ul className="nav nav-tabs border-bottom-0 d-flex flex-nowrap" style={{ gap: '0px' }}>
          {(() => {
            const checkIsFile = (c) => c && (c.file || c.file_path || c.file_name || (c.content && /\.(gif|jpg|jpeg|png|pdf|zip|doc|docx|xls|xlsx|ppt|pptx|txt|hwp|csv|ai|psd)$/i.test(c.content.trim())));
            const visibleFilesCount = comments.filter(c => {
              if (!checkIsFile(c)) return false;
              if (!c.parent_id) return true;
              const parent = comments.find(p => p.id === c.parent_id);
              return checkIsFile(parent);
            }).length;

            const getVisibleCommentsCount = () => {
              let count = 0;
              const countReplies = (parentId) => {
                const children = comments.filter(c => c.parent_id === parentId);
                count += children.length;
                children.forEach(child => countReplies(child.id));
              };
              const rootComments = comments.filter(c => !c.parent_id);
              count += rootComments.length;
              rootComments.forEach(root => countReplies(root.id));
              return count;
            };

            return [
              { id: 'checklist', name: '체크리스트', icon: 'fas fa-check-square', count: checklists.length },
              { id: 'comments', name: '코멘트', icon: 'far fa-comments', count: getVisibleCommentsCount() },
              { id: 'files', name: '파일', icon: 'far fa-folder-open', count: visibleFilesCount },
              { id: 'settings', name: '세부설정', icon: 'fas fa-cog' }
            ].map((tab, idx) => (
              <li className="nav-item" key={tab.id}>
                <button
                  className={`nav-link d-flex align-items-center justify-content-center ${activeTab === tab.id ? 'fw-bold text-primary' : 'fw-semibold text-dark'}`}
                  style={{
                    borderTop: activeTab === tab.id ? '3px solid #0d6efd' : '1px solid #dee2e6',
                    borderLeft: '1px solid #dee2e6',
                    borderRight: '1px solid #dee2e6',
                    borderBottom: activeTab === tab.id ? '1px solid #fff' : '1px solid #dee2e6',
                    backgroundColor: activeTab === tab.id ? '#ffffff' : '#e9ecef',
                    borderRadius: '4px 4px 0 0',
                    marginLeft: idx > 0 ? '-1px' : '0',
                    marginBottom: '-1px',
                    padding: '10px 24px',
                    fontSize: '0.9rem',
                    position: 'relative',
                    transition: 'background-color 0.2s',
                    zIndex: activeTab === tab.id ? 2 : 1
                  }}
                  onMouseEnter={(e) => { if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = '#d3d9df'; }}
                  onMouseLeave={(e) => { if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = '#e9ecef'; }}
                  onClick={() => handleTabChange(tab.id)}>
                  <i className={`${tab.icon} me-2 ${activeTab === tab.id ? 'text-primary' : 'text-muted'}`}></i>
                  {tab.name}
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className={`badge ms-2 rounded-pill ${activeTab === tab.id ? 'bg-primary' : 'bg-secondary'}`} style={{ fontSize: '0.7rem' }}>{tab.count}</span>
                  )}
                </button>
              </li>
            ))
          })()}
        </ul>

        {/* 우측 상단 집중모드(전체보기) 버튼 */}
        {((activeTab === 'comments' && comments.length > 0) ||
          (activeTab === 'checklist' && checklists.length > 0) ||
          (activeTab === 'settings') ||
          (activeTab === 'files' && comments.some(c => c.parent_id === null && c.content && (c.content.includes('.gif') || c.content.includes('.jpg') || c.content.includes('.pdf') || c.content.includes('.png'))))) && (
            <button
              className="btn btn-sm mb-1 px-3 fw-bold rounded-pill shadow-sm"
              style={{
                backgroundColor: isFullScreenMode ? '#4c6ef5' : '#e8f0fe',
                color: isFullScreenMode ? '#ffffff' : '#1a73e8',
                border: isFullScreenMode ? '1px solid #4c6ef5' : '1px solid #d2e3fc',
                fontSize: '0.8rem',
                transition: 'all 0.2s ease-in-out'
              }}
              onClick={() => setIsFullScreenMode(!isFullScreenMode)}
            >
              {isFullScreenMode ? (
                <><i className="fas fa-compress-arrows-alt me-1"></i> 기본보기</>
              ) : (
                <><i className="fas fa-expand-arrows-alt me-1"></i> 확장보기</>
              )}
            </button>
          )}
      </div>

      {/* 🚀 탭 본문 영역 (하얀색 바탕) */}
      <div className="flex-grow-1 overflow-hidden p-4 bg-white border-top">

        {/* =========================================
            [1] 체크리스트 탭 (Checklist - 도미노 완벽 구현)
            ========================================= */}
        {activeTab === 'checklist' && (
          <ChecklistTab
            project={project}
            task={task}
            checklists={checklists}
            newChecklist={newChecklist}
            setNewChecklist={setNewChecklist}
            handleAddChecklist={handleAddChecklist}
            handleToggleChecklist={handleToggleChecklist}
            handleEditChecklist={handleEditChecklist}
            handleDeleteChecklist={handleDeleteChecklist}
            isFullScreenMode={isFullScreenMode}
            currentUser={user}
            isProjectMaster={isProjectMaster}
            isAssignee={isAssignee}
            isPureWatcher={isPureWatcher}
          />
        )}

        {/* =========================================
            [2] 코멘트 탭 (Comments & Timeline)
            ========================================= */}
        {activeTab === 'comments' && (
          <CommentsTab
            project={project}
            task={task}
            focusId={focusId}
            comments={comments}
            newComment={newComment}
            setNewComment={setNewComment}
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
            fileInputRef={fileInputRef}
            handleAddComment={handleAddComment}
            handleDeleteComment={handleDeleteComment}
            handleEditCommentSubmit={handleEditCommentSubmit}
            openFolderModal={openFolderModal}
            replyTargetId={replyTargetId}
            setReplyTargetId={setReplyTargetId}
            replyContent={replyContent}
            setReplyContent={setReplyContent}
            handleReplySubmit={handleReplySubmit}
            isFullScreenComments={isFullScreenMode}
            currentUser={user}
            folders={folders}
            isPureWatcher={isPureWatcher}
            isProjectMaster={isProjectMaster}
          />
        )}

        {/* =========================================
            [4] 설정 탭 (Settings - Summary & Progress)
            ========================================= */}
        {activeTab === 'settings' && (
          <SettingsTab
            task={task}
            progressRate={progressRate}
            setProgressRate={setProgressRate}
            handleSaveProgress={handleSaveProgress}
            currentUser={user}
            project={project}
            onClose={onClose}
            onDeleteTask={handleDeleteTask}
            hasEditPermission={isProjectMaster || isAssignee}
            categoryName={categoryName}
          />
        )}

        {/* =========================================
            [3] 파일 탭 (Files Cloud View - 도미노 오리지널 뷰)
            ========================================= */}
        {activeTab === 'files' && (
          <FilesTab
            folders={folders}
            setFolders={setFolders}
            newFolderName={newFolderName}
            setNewFolderName={setNewFolderName}
            handleCreateFolder={handleCreateFolder}
            comments={comments}
            handleDeleteComment={handleDeleteComment}
            openFolderModal={(commentId) => {
              setTargetFileId(commentId);
              setShowFolderModal(true);
            }}
            openEditFolderModal={openEditFolderModal}
            openDeleteFolderModal={openDeleteFolderModal}
            handleDragMoveFolder={handleDragMoveFolder}
            replyTargetId={replyTargetId}
            setReplyTargetId={setReplyTargetId}
            replyContent={replyContent}
            setReplyContent={setReplyContent}
            handleReplySubmit={handleReplySubmit}
            isFullScreenFiles={isFullScreenMode}
            currentUser={user}
            isProjectMaster={isProjectMaster}
          />
        )}
      </div>

      {/* 이동할 폴더 선택 모달 */}
      {showFolderModal && (
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* 전역 투명 쉴드: 버튼 클릭을 가리지 않게 모달 뒤로(-1) 배치하며 왼쪽 화면 방어 */}
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: -1, backgroundColor: 'transparent' }} onClick={(e) => e.stopPropagation()}></div>

          {/* 우측 패널 딤 배경 */}
          <div className="modal-backdrop fade show" style={{ opacity: 0.5, position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#000', zIndex: 0 }}></div>

          {/* 모달 창 */}
          <div className="modal-content shadow-lg border-0" style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '420px', borderRadius: '12px', overflow: 'hidden' }}>
            {/* 헤더: 연한 회색 배경, 짙은 텍스트, 폴더 아이콘 */}
            <div className="modal-header py-3 px-4 border-bottom d-flex justify-content-between align-items-center" style={{ backgroundColor: '#f1f3f5', borderColor: '#e9ecef' }}>
              <h6 className="modal-title fw-bolder text-dark d-flex align-items-center mb-0" style={{ fontSize: '1.05rem', letterSpacing: '-0.3px' }}>
                <i className="far fa-folder-open me-2" style={{ color: '#4c6ef5', fontSize: '1.2rem' }}></i>
                이동할 폴더 선택
              </h6>
              <button type="button" className="btn btn-light rounded-circle text-muted border-0 shadow-none d-flex align-items-center justify-content-center"
                onClick={() => setShowFolderModal(false)}
                style={{ width: '28px', height: '28px', backgroundColor: '#e9ecef' }}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* 본문: 트렌디한 리스트 그룹 디자인 */}
            <div className="modal-body p-3 bg-white">
              {folders.length === 0 ? (
                <div className="text-center text-muted py-4 border rounded-3" style={{ backgroundColor: '#f8f9fa', borderColor: '#e5e7eb' }}>
                  <p className="mb-0 fs-7">이동할 폴더가 없습니다.</p>
                  <p className="mb-0 fs-7 text-primary cursor-pointer mt-1" onClick={() => { setShowFolderModal(false); setActiveTab('files'); }} style={{ textDecoration: 'underline' }}>[파일] 탭에서 생성해주세요.</p>
                </div>
              ) : (
                <div className="border rounded-3 overflow-hidden shadow-sm" style={{ borderColor: '#e5e7eb' }}>
                  <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                    {/* 최상위(기본) 폴더로 빼기 옵션 */}
                    <div className="d-flex align-items-center px-3 py-2 position-relative border-bottom"
                      onClick={() => {
                        const targetFile = comments.find(c => String(c.id) === String(targetFileId));
                        if (targetFile && (targetFile.folder_name || '') === '') {
                          alert('현재 이미 최상위에 위치하고 있습니다.');
                          return;
                        }
                        setSelectedFolder('');
                      }}
                      onMouseEnter={(e) => { if (selectedFolder !== '') e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                      onMouseLeave={(e) => { if (selectedFolder !== '') e.currentTarget.style.backgroundColor = '#ffffff'; }}
                      style={{
                        borderColor: '#f1f3f5',
                        backgroundColor: selectedFolder === '' ? '#f0f4ff' : '#ffffff',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease'
                      }}>
                      <div className="fw-bold fs-7 d-flex align-items-center flex-grow-1">
                        <div className="d-flex align-items-center justify-content-center me-3 shadow-sm"
                          style={{ width: '28px', height: '28px', borderRadius: '8px', backgroundColor: selectedFolder === '' ? '#4c6ef5' : '#f1f3f5', transition: 'all 0.2s ease' }}>
                          <i className={`fas fa-reply ${selectedFolder === '' ? 'text-white' : 'text-secondary'}`} style={{ fontSize: '0.8rem', display: 'flex' }}></i>
                        </div>
                        <span style={{ color: selectedFolder === '' ? '#4c6ef5' : '#495057', transition: 'color 0.2s ease' }}>최상위로 이동</span>
                      </div>
                      {selectedFolder === '' && (
                        <div className="animation-fade-in ms-2">
                          <i className="fas fa-check" style={{ color: '#4c6ef5', fontSize: '0.9rem' }}></i>
                        </div>
                      )}
                    </div>
                    {folders.map((f, idx) => (
                      <div className={`d-flex align-items-center px-3 py-2 position-relative ${idx !== folders.length - 1 ? 'border-bottom' : ''}`} key={idx}
                        onClick={() => {
                          const targetFile = comments.find(c => String(c.id) === String(targetFileId));
                          if (targetFile && (targetFile.folder_name || '') === f) {
                            alert('현재 위치와 동일한 폴더입니다.');
                            return;
                          }
                          setSelectedFolder(f);
                        }}
                        onMouseEnter={(e) => { if (selectedFolder !== f) e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                        onMouseLeave={(e) => { if (selectedFolder !== f) e.currentTarget.style.backgroundColor = '#ffffff'; }}
                        style={{
                          borderColor: '#f1f3f5',
                          backgroundColor: selectedFolder === f ? '#f0f4ff' : '#ffffff',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease'
                        }}>
                        {/* 촌스러운 라디오 버튼 완전 숨김 */}
                        <input type="radio" className="d-none" name="folderSelect" value={f} checked={selectedFolder === f} onChange={() => { }} />

                        <div className="fw-bold fs-7 d-flex align-items-center flex-grow-1">
                          {/* Mac 스타일 폴더 아이콘 박스 */}
                          <div className="d-flex align-items-center justify-content-center me-3 shadow-sm"
                            style={{ width: '28px', height: '28px', borderRadius: '8px', backgroundColor: selectedFolder === f ? '#4c6ef5' : '#f1f3f5', transition: 'all 0.2s ease' }}>
                            <i className={`fas ${selectedFolder === f ? 'fa-folder-open text-white' : 'fa-folder text-secondary'}`} style={{ fontSize: '0.8rem', display: 'flex' }}></i>
                          </div>
                          <span style={{ color: selectedFolder === f ? '#4c6ef5' : '#495057', transition: 'color 0.2s ease' }}>{f}</span>
                        </div>

                        {/* 선택 시 우측 세련된 체크 마크 */}
                        {selectedFolder === f && (
                          <div className="animation-fade-in ms-2">
                            <i className="fas fa-check" style={{ color: '#4c6ef5', fontSize: '0.9rem' }}></i>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* 푸터: 취소 및 저장 버튼 구성 */}
            <div className="modal-footer border-top py-3 px-4 d-flex justify-content-end" style={{ backgroundColor: '#f1f3f5', borderColor: '#e9ecef', gap: '3px' }}>
              <button type="button" className="btn btn-secondary border-0 btn-sm fw-bold shadow-sm"
                style={{ backgroundColor: '#707077', borderRadius: '6px', fontSize: '0.8rem', padding: '6px 16px' }}
                onClick={() => setShowFolderModal(false)}>취소</button>
              {folders.length > 0 && (
                <button type="button" className="btn btn-primary border-0 btn-sm fw-bold shadow-sm d-flex align-items-center gap-1"
                  style={{ backgroundColor: '#4c6ef5', borderRadius: '6px', fontSize: '0.8rem', padding: '6px 16px' }}
                  onClick={handleMoveFolder}>
                  <i className="fas fa-check" style={{ fontSize: '0.75rem' }}></i> 저장
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 🚀 폴더명 변경 커스텀 모달 (오른쪽 패널 전체 덮기) */}
      {editFolderModalVisible && (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1050 }}>
          <div className="bg-white rounded-3 shadow-lg" style={{ width: '400px', maxWidth: '90%' }}>
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom rounded-top" style={{ backgroundColor: '#f1f3f5' }}>
              <h6 className="m-0 fw-bold d-flex align-items-center text-dark">
                <i className="far fa-edit me-2" style={{ color: '#4c6ef5', fontSize: '1.2rem', fontWeight: 'bold' }}></i>폴더명 변경
              </h6>
              <button className="btn-close" onClick={() => setEditFolderModalVisible(false)}></button>
            </div>
            <div className="p-4 bg-white">
              <label className="form-label text-muted fs-7 fw-bold mb-2">변경할 폴더명</label>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0"><i className="fas fa-rocket" style={{ color: '#ff8787' }}></i></span>
                <input type="text" className="form-control border-start-0 ps-0 shadow-none" value={newEditFolderName} onChange={(e) => setNewEditFolderName(e.target.value)} />
              </div>
            </div>
            <div className="p-3 border-top d-flex justify-content-end gap-2 rounded-bottom" style={{ backgroundColor: '#f1f3f5' }}>
              <button className="btn border-0 text-white fw-bold shadow-sm" style={{ backgroundColor: '#6c757d', borderRadius: '4px', padding: '6px 18px', fontSize: '0.85rem' }} onClick={() => setEditFolderModalVisible(false)}>취소</button>
              <button className="btn border-0 text-white fw-bold shadow-sm d-flex align-items-center gap-1" style={{ backgroundColor: '#4263eb', borderRadius: '4px', padding: '6px 18px', fontSize: '0.85rem' }} onClick={handleEditFolderSubmit}>
                <i className="fas fa-check"></i> 변경
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2) 폴더 삭제 모달 (오른쪽 패널 전체 덮기) */}
      {deleteFolderModalVisible && (() => {
        // 폴더 안에 파일이 들어있는지 체크 (댓글 포함)
        const hasFiles = comments.some(c => c.folder_name === selectedDeleteFolder);

        return (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1050 }}>
            <div className="bg-white rounded-3 shadow-lg animation-fade-in-up" style={{ width: '400px', maxWidth: '90%' }}>

              <div className="d-flex justify-content-between align-items-center p-3 border-bottom rounded-top" style={{ backgroundColor: '#f1f3f5' }}>
                <h5 className="mb-0 fs-6 fw-bold text-dark">
                  <i className="fas fa-trash-alt text-danger me-2"></i> 폴더 삭제
                </h5>
                <button type="button" className="btn-close shadow-none" onClick={() => setDeleteFolderModalVisible(false)}></button>
              </div>

              <div className="p-4 text-center">
                <p className="mb-3 text-dark fw-bold fs-6">
                  <span className="text-danger">[{selectedDeleteFolder}]</span> 폴더를 삭제하시겠습니까?
                </p>
                {hasFiles ? (
                  <>
                    <p className="text-muted fs-8 mb-4">
                      폴더만 삭제하거나 폴더 안의 파일들까지 한 번에 영구 삭제할 수 있습니다.
                    </p>
                    <div className="d-grid gap-2">
                      <button className="btn btn-outline-danger fw-bold py-2 shadow-sm" onClick={() => handleDeleteFolderSubmit('folder_only')}>
                        폴더만 삭제 (내부 파일은 유지)
                      </button>
                      <button className="btn btn-danger fw-bold py-2 shadow-sm" onClick={() => handleDeleteFolderSubmit('folder_and_files')}>
                        폴더와 내부 파일 모두 영구 삭제
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-muted fs-8 mb-4">
                      현재 이 폴더는 비어 있습니다.<br />폴더를 삭제하시겠습니까?
                    </p>
                    <div className="d-grid gap-2">
                      <button className="btn btn-danger fw-bold py-2 shadow-sm" onClick={() => handleDeleteFolderSubmit('folder_only')}>
                        폴더 삭제
                      </button>
                    </div>
                  </>
                )}
              </div>

            </div>
          </div>
        );
      })()}

      {/* 3) 세부항목(Task) 삭제 모달 */}
      {deleteTaskModalVisible && (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1050 }}>
          <div className="bg-white rounded-3 shadow-lg animation-fade-in-up" style={{ width: '400px', maxWidth: '90%' }}>

            <div className="d-flex justify-content-between align-items-center p-3 border-bottom rounded-top" style={{ backgroundColor: '#f1f3f5' }}>
              <h5 className="mb-0 fs-6 fw-bold text-dark">
                <i className="fas fa-trash-alt text-danger me-2"></i> 세부항목 삭제
              </h5>
              <button type="button" className="btn-close shadow-none" onClick={() => setDeleteTaskModalVisible(false)}></button>
            </div>

            <div className="p-4 text-center">
              <p className="mb-3 text-dark fw-bold fs-6 lh-base">
                <span className="text-danger">[{task.title}]</span> 항목을 정말 삭제하시겠습니까?
              </p>
              <p className="text-muted fs-8 mb-4 lh-base" style={{ wordBreak: 'keep-all' }}>
                이 항목을 삭제하면 내부에 작성된 <strong>체크리스트({(checklists || []).length}개), 코멘트 및 첨부파일({(comments || []).length}개) 등 모든 데이터가 함께 영구 삭제</strong>되며, 절대 복구할 수 없습니다.
              </p>
              <div className="d-grid gap-2">
                <button className="btn btn-danger fw-bold py-2 shadow-sm" onClick={handleDeleteTaskSubmit}>
                  일괄 영구 삭제
                </button>
                <button className="btn btn-outline-secondary fw-bold py-2 shadow-sm" onClick={() => setDeleteTaskModalVisible(false)}>
                  취소 (닫기)
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}

export default TaskDrawerPanel;
